import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Colors } from '../../constants/colors';
import { Device, DeviceWidth } from '../../constants/device';
import { useIsNarrowerThan } from '../../helpers/useIsMobile';
import { mapBoxes, cityOutlines, mapCallouts, mapCities, mapInsetCities, mapInsets, mapLandmarks, mapOnlyDeployments, networkLogos, standaloneDeployments } from '../../config/deploymentsConfig';
import { countryShapes, mapSize, projectToMap, riversPath } from '../../config/deploymentsMapShapes';
import { Country, Deployment, Network } from '../../model/Deployment';

interface Site extends Deployment {
  x: number;
  y: number;
}

type InsetKey = (typeof mapInsets)[number]['key'];

const countries: Country[] = ['cz', 'sk', 'hu', 'hr'];

// Each country with deployments has a colour of its own, the neighbours stay grey.
const countryColors: Record<Country, string> = { cz: Colors.primary, sk: '#16917f', hu: '#8150c0', hr: '#c0473f' };

// Delay between two sites lighting up, and the size of the logo callouts, in ms and map units.
const siteDelay = 14;
const calloutWidth = 96;
const calloutHeight = 34;

const sites: Site[] = [...mapOnlyDeployments, ...standaloneDeployments]
  .filter((deployment) => deployment.latitude && deployment.longitude)
  .map((deployment) => {
    const [x, y] = projectToMap(deployment.latitude!, deployment.longitude!);
    return { ...deployment, x, y };
  })
  // West to east, so that the sites light up across the map in that order.
  .sort((a, b) => a.x - b.x);

// Where a city's name sits relative to the city, in map units, and how it is anchored.
const cityLabelOffsets = {
  left: { dx: -6, dy: 3.5, anchor: 'end' },
  right: { dx: 6, dy: 3.5, anchor: 'start' },
  above: { dx: 0, dy: -6, anchor: 'middle' },
  below: { dx: 0, dy: 13, anchor: 'middle' },
};

const boxes = mapBoxes.map((box) => {
  const [sourceX, sourceY] = projectToMap(box.latitude, box.longitude);
  const toBox = (x: number, y: number): [number, number] => [box.x + (x - sourceX) * box.zoom, box.y + (y - sourceY) * box.zoom];
  return {
    ...box,
    toBox,
    // Whether a point on the map is in the area the box shows.
    contains: (x: number, y: number) => Math.abs(x - sourceX) * box.zoom <= box.width / 2 && Math.abs(y - sourceY) * box.zoom <= box.height / 2,
    // Moves, and enlarges, the area around the box's source point into the box.
    transform: `translate(${box.x} ${box.y}) scale(${box.zoom}) translate(${-sourceX} ${-sourceY})`,
  };
});
const boxOf = (point: { x: number; y: number }) => boxes.find((box) => box.contains(point.x, point.y));

// Cities in a box are drawn in the box, the others on the map.
const cities = mapCities.map((city) => {
  const [x, y] = projectToMap(city.latitude, city.longitude);
  const box = boxOf({ x, y });
  const [boxX, boxY] = box ? box.toBox(x, y) : [x, y];
  // A city bigger than its point on the map is drawn as its outline, with the name beside it.
  const outline = cityOutlines[city.key]?.map(([latitude, longitude]) => projectToMap(latitude, longitude));
  return { ...city, x: boxX, y: boxY, outline };
});

// Where a city's name goes: beside its point, or beside its outline when it has one.
const cityLabelPosition = ({ x, y, side, outline }: (typeof cities)[number]) => {
  const offset = cityLabelOffsets[side];
  if (!outline) {
    return { x: x + offset.dx, y: y + offset.dy, anchor: offset.anchor };
  }
  const xs = outline.map(([px]) => px);
  const ys = outline.map(([, py]) => py);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const positions = {
    left: { x: minX - 3, y: y + 3.5 },
    right: { x: maxX + 3, y: y + 3.5 },
    above: { x, y: minY - 4 },
    below: { x, y: maxY + 11 },
  };
  return { ...positions[side], anchor: offset.anchor };
};

const pathOf = (points: [number, number][]) => `M${points.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join('L')}Z`;

// Ray casting: whether a point lies inside a polygon.
const insidePolygon = (x: number, y: number, polygon: [number, number][]) =>
  polygon.reduce((inside, [x1, y1], index) => {
    const [x2, y2] = polygon[(index + polygon.length - 1) % polygon.length];
    return y1 > y !== y2 > y && x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1 ? !inside : inside;
  }, false);

const insets = mapInsets.map((inset) => {
  const [sourceX, sourceY] = projectToMap(inset.latitude, inset.longitude);
  const toInset = (x: number, y: number): [number, number] => [inset.x + (x - sourceX) * inset.zoom, inset.y + (y - sourceY) * inset.zoom];
  // The area on the map, and enlarged: a city's outline, or a circle.
  const outline = inset.outline?.map(([latitude, longitude]) => projectToMap(latitude, longitude));
  const radius = inset.radius ?? 0;
  const contains = (x: number, y: number) => (outline ? insidePolygon(x, y, outline) : Math.hypot(x - sourceX, y - sourceY) <= radius);
  // The two outer tangents between the area and its enlargement, drawing the two as one
  // magnifying glass. The enlargement is the same shape, so both tangents pass through
  // the centre of similitude and touch the two shapes at matching points.
  let tangents: { x1: number; y1: number; x2: number; y2: number }[];
  if (outline) {
    const [hx, hy] = [(inset.x - inset.zoom * sourceX) / (1 - inset.zoom), (inset.y - inset.zoom * sourceY) / (1 - inset.zoom)];
    const toCentre = Math.atan2(sourceY - hy, sourceX - hx);
    const angleOf = ([x, y]: [number, number]) => Math.atan2(Math.sin(Math.atan2(y - hy, x - hx) - toCentre), Math.cos(Math.atan2(y - hy, x - hx) - toCentre));
    const byAngle = [...outline].sort((a, b) => angleOf(a) - angleOf(b));
    tangents = [byAngle[0], byAngle[byAngle.length - 1]].map(([x, y]) => {
      const [x2, y2] = toInset(x, y);
      return { x1: x, y1: y, x2, y2 };
    });
  } else {
    const enlargedRadius = radius * inset.zoom;
    const distance = Math.hypot(inset.x - sourceX, inset.y - sourceY);
    const direction = Math.atan2(inset.y - sourceY, inset.x - sourceX);
    const spread = Math.acos((radius - enlargedRadius) / distance);
    tangents = [direction + spread, direction - spread].map((angle) => ({
      x1: sourceX + radius * Math.cos(angle),
      y1: sourceY + radius * Math.sin(angle),
      x2: inset.x + enlargedRadius * Math.cos(angle),
      y2: inset.y + enlargedRadius * Math.sin(angle),
    }));
  }
  return {
    ...inset,
    sourceX,
    sourceY,
    radius,
    contains,
    toInset,
    tangents,
    // The area on the map and its enlargement, as paths when it is an outline.
    sourcePath: outline && pathOf(outline),
    enlargedPath: outline && pathOf(outline.map(([x, y]) => toInset(x, y))),
    // Enlarges the map around the inset's source point into the inset.
    transform: `translate(${inset.x} ${inset.y}) scale(${inset.zoom}) translate(${-sourceX} ${-sourceY})`,
  };
});
type Inset = (typeof insets)[number];
const insetOf = (site: { x: number; y: number }): Inset | undefined => insets.find((inset) => inset.contains(site.x, site.y));
const insetByKey = (key: InsetKey): Inset => insets.find((inset) => inset.key === key)!;

// Prague landmarks as small silhouettes standing on their spot, drawn around 0, 0.
const landmarkShapes = {
  castle: 'M-7 0V-3H-4V-6H-2.5V-9L-1.5 -12.5L-0.5 -9V-6H1V-8L2 -10L3 -8V-3H7V0Z',
  // The Petřín lookout tower, a small Eiffel tower.
  petrin:
    'M-5 0C-3.6 -3.6 -2.4 -7 -1.7 -9.6H-2.6V-10.8H-1.4C-1 -13.2 -0.6 -15.4 -0.4 -17.2H-0.9V-18H-0.3L0 -21L0.3 -18H0.9V-17.2H0.4C0.6 -15.4 1 -13.2 1.4 -10.8H2.6V-9.6H1.7C2.4 -7 3.6 -3.6 5 0H3.3C2.6 -2.4 1.5 -3.8 0 -3.8C-1.5 -3.8 -2.6 -2.4 -3.3 0Z',
  vysehrad: 'M-7 0Q0 -4 7 0Z M-3.2 -2V-8L-2.2 -11.5L-1.2 -8V-2Z M1.2 -2V-8L2.2 -11.5L3.2 -8V-2Z',
  // Jan Žižka on horseback with his mace raised, the statue on Vítkov.
  vitkov: [
    'M-7.5 0V-3H7.5V0Z',
    'M-4.4 -3H-3.2V-6.8H-4.4Z M-2.6 -3H-1.4V-6.8H-2.6Z M1.8 -3H3V-6.8H1.8Z M3.5 -3H4.7V-6.8H3.5Z',
    'M-5.2 -6.4C-5.8 -8.8 -4.9 -11.2 -2.8 -11.4H3.2C5.2 -11.2 5.8 -8.6 5 -6.4Z',
    'M-4.6 -9.4L-5.8 -13.4L-8.2 -12.2L-8.6 -13.2L-6.4 -15.2L-4.9 -15L-2.6 -11Z',
    'M4.8 -10.6C6.4 -10.2 6.8 -8.2 6.4 -6.2L5.6 -6.4C5.8 -8 5.4 -9.4 4.6 -9.6Z',
    'M-1.6 -11.2L-1 -16.2H1.2L1.8 -11.2Z M-0.3 -11.2L0.3 -8.2H1.3L1 -11.2Z',
    'M0.1 -18.5A1.2 1.2 0 1 0 0.11 -18.5Z',
    'M0.6 -15.4L2.9 -18.7L3.5 -18.3L1.3 -15Z M3.1 -18.6L3.5 -20.5L4.4 -20.3L3.9 -18.4Z',
  ].join(' '),
  // Lysá hora: a broad bald dome with a lower ridge beside it and the slender
  // transmitter tower on the summit, its cabin and antenna on top.
  lysaHora: [
    'M-13 0C-10 -4 -6 -8.5 0 -8.5C6 -8.5 10 -4 13 0Z',
    'M8 0C10 -2.5 13 -4 16 -3.5C18 -3 19 -1.5 20 0Z',
    'M-1 -8L-0.6 -19H0.6L1 -8Z',
    'M-1.5 -16.5H1.5V-14H-1.5Z',
    'M-0.25 -19V-22.5H0.25V-19Z',
  ].join(' '),
};
const insetCities = mapInsetCities.map((city) => {
  const inset = insetByKey(city.inset);
  const [x, y] = inset.toInset(...projectToMap(city.latitude, city.longitude));
  const outline = cityOutlines[city.key]?.map(([latitude, longitude]) => inset.toInset(...projectToMap(latitude, longitude)));
  return { ...city, x, y, outline };
});
const landmarks = mapLandmarks.map(({ dx = 0, dy = 0, ...landmark }) => {
  const [x, y] = insetByKey(landmark.inset).toInset(...projectToMap(landmark.latitude, landmark.longitude));
  return { ...landmark, x: x + dx, y: y + dy };
});

// Pushes points closer than `gap` apart until each can be hovered or tapped on its own.
// Points at the very same place start around it, so that they have a direction to move in.
const spreadApart = (points: Site[], gap: number): Site[] => {
  const spread = points.map((point, index) => {
    const twins = points.filter((other) => other.x === point.x && other.y === point.y);
    const angle = (twins.indexOf(point) / twins.length) * 2 * Math.PI + index;
    return twins.length > 1 ? { ...point, x: point.x + Math.cos(angle), y: point.y + Math.sin(angle) } : { ...point };
  });
  for (let round = 0; round < 50; round++) {
    spread.forEach((a, i) =>
      spread.slice(i + 1).forEach((b) => {
        const distance = Math.hypot(b.x - a.x, b.y - a.y);
        if (distance < gap) {
          const push = (gap - distance) / 2 / distance;
          const [dx, dy] = [(b.x - a.x) * push, (b.y - a.y) * push];
          a.x -= dx;
          a.y -= dy;
          b.x += dx;
          b.y += dy;
        }
      }),
    );
  }
  return spread;
};

// Where sites are too dense to tell apart (Prague, around Ostrava) they are drawn,
// and linked, only enlarged in an inset, spread apart where they would still overlap.
// Sites elsewhere in the same town (a hospital and its laboratory, say) are spread
// apart on the map, so each shows as its own point.
const mapSites = spreadApart(
  sites.filter((site) => !insetOf(site) && !boxOf(site)),
  10,
);
// Sites too far off the map are drawn in a box of their own.
const boxSites: Site[] = sites.flatMap((site) => {
  const box = boxOf(site);
  if (!box) {
    return [];
  }
  const [x, y] = box.toBox(site.x, site.y);
  return [{ ...site, x, y }];
});
const insetSites: (Site & { inset: InsetKey })[] = insets.flatMap((inset) =>
  spreadApart(
    sites
      .filter((site) => insetOf(site) === inset)
      .map((site) => {
        const [x, y] = inset.toInset(site.x, site.y);
        return { ...site, x, y };
      }),
    12,
  ).map((site) => ({ ...site, inset: inset.key })),
);

// A callout points at its site on the map, or at the one in an inset or a box.
const callouts = mapCallouts.map(({ name, dx, dy }) => {
  const insetSite = [...insetSites, ...boxSites].find((candidate) => candidate.name === name);
  const site = insetSite ?? mapSites.find((candidate) => candidate.name === name)!;
  return { site, x: site.x + dx, y: site.y + dy, inset: !!insetSite };
});

// Minimum spanning tree of sites (Prim's algorithm): the shortest set of lines
// connecting them all, drawn as a constellation.
const spanningTree = (members: Site[]): [Site, Site][] => {
  const distance = (a: Site, b: Site) => Math.hypot(a.x - b.x, a.y - b.y);
  const connected = members.slice(0, 1);
  const rest = members.slice(1);
  const lines: [Site, Site][] = [];
  while (rest.length) {
    let best: [Site, number] | undefined;
    for (const from of connected) {
      rest.forEach((to, index) => {
        if (!best || distance(from, to) < distance(best[0], rest[best[1]])) {
          best = [from, index];
        }
      });
    }
    const [from, index] = best!;
    lines.push([from, rest[index]]);
    connected.push(rest.splice(index, 1)[0]);
  }
  return lines;
};

// A network's constellation: on the map its sites there, with its sites in an inset
// joined in at the centre of the enlarged area, and in each inset its sites there.
const constellation = (network: Network): { map: [Site, Site][]; inset: [Site, Site][] } => {
  const members = insetSites.filter((site) => site.network === network);
  const areas = insets
    .filter((inset) => members.some((site) => site.inset === inset.key))
    .map((inset) => ({ ...members.find((site) => site.inset === inset.key)!, name: inset.key, x: inset.sourceX, y: inset.sourceY }));
  return {
    map: spanningTree([...mapSites.filter((site) => site.network === network), ...areas]),
    inset: insets.flatMap((inset) => spanningTree(members.filter((site) => site.inset === inset.key))),
  };
};

/**
 * Map of the deployments in the Deployments section: a dotted map of Central
 * Europe with rivers and major cities for orientation, a point for every site
 * and the logos of some of them. Prague, too dense to tell its sites apart, is
 * also shown enlarged in a circle. The points light up from west to east once
 * the map scrolls into view; hovering a site of a healthcare network draws the
 * whole network as a constellation.
 */
export const DeploymentsMap = (): ReactElement => {
  const { t } = useTranslation();
  const frame = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState<boolean>(false);
  const [hovered, setHovered] = useState<Site>();
  // Hovering a logo callout highlights its site without the card, the callout already shows the logo.
  const [showCard, setShowCard] = useState<boolean>(false);
  const activeNetwork = hovered?.network;
  const lines = useMemo(() => (activeNetwork ? constellation(activeNetwork) : { map: [], inset: [] }), [activeNetwork]);

  useEffect(() => {
    if (!frame.current || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(frame.current);
    return () => observer.disconnect();
  }, []);

  const hover = (site: Site | undefined, card: boolean) => {
    setHovered(site);
    setShowCard(card);
  };

  // A site with a callout opens its card right over the callout, as if the callout
  // grew to show the name too. Phones hide the callouts, so there it opens at the point.
  const calloutsShown = !useIsNarrowerThan(DeviceWidth.mobile);
  const hoveredCallout = calloutsShown && hovered ? callouts.find(({ site }) => site.name === hovered.name) : undefined;

  // A mouse hovers a site to see its card and clicks it to open its website.
  // Touch has no hover, so a tap only shows the card and a tap elsewhere on the
  // map hides it; the website is never opened by a tap.
  const pointer = useRef<string>('mouse');
  const interactions = (site: Site, card: boolean) => ({
    onPointerDown: (event: React.PointerEvent) => {
      pointer.current = event.pointerType;
    },
    onPointerEnter: (event: React.PointerEvent) => event.pointerType === 'mouse' && hover(site, card),
    onPointerLeave: (event: React.PointerEvent) => event.pointerType === 'mouse' && hover(undefined, false),
    onFocus: () => hover(site, card),
    onBlur: () => hover(undefined, false),
    onClick: (event: React.MouseEvent) => {
      if (pointer.current !== 'mouse') {
        event.preventDefault();
        hover(site, true);
      }
      pointer.current = 'mouse';
    },
  });

  const hideOnTapOutside = (event: React.MouseEvent) => {
    if (!(event.target as Element).closest('a')) {
      hover(undefined, false);
    }
  };

  return (
    <MapFrame ref={frame} $revealed={revealed} onClick={hideOnTapOutside}>
      <svg viewBox={`0 0 ${mapSize.width} ${mapSize.height}`} role="group" aria-label={t('deployments.map.label')}>
        <defs>
          {countries.map((country) => (
            <pattern key={country} id={`deployments-dots-${country}`} width={7} height={7} patternUnits="userSpaceOnUse">
              <circle cx={3.5} cy={3.5} r={1.7} fill={countryColors[country]} fillOpacity={0.42} />
            </pattern>
          ))}
          <pattern id="deployments-dots-faint" width={7} height={7} patternUnits="userSpaceOnUse">
            <circle cx={3.5} cy={3.5} r={1.1} fill={Colors.lightGrey} fillOpacity={0.45} />
          </pattern>
          {/* The map fades out towards its edges instead of ending in a frame. */}
          <radialGradient id="deployments-fade" cx="45%" cy="45%" r="62%">
            <stop offset="70%" stopColor="white" />
            <stop offset="100%" stopColor="black" />
          </radialGradient>
          <mask id="deployments-mask">
            <rect width={mapSize.width} height={mapSize.height} fill="url(#deployments-fade)" />
          </mask>
          {insets.map((inset) => (
            <clipPath key={inset.key} id={`deployments-inset-${inset.key}`}>
              {inset.enlargedPath ? <path d={inset.enlargedPath} /> : <circle cx={inset.x} cy={inset.y} r={inset.radius * inset.zoom} />}
            </clipPath>
          ))}
          {boxes.map((box) => (
            <React.Fragment key={box.key}>
              <clipPath id={`deployments-box-${box.key}`}>
                <rect x={box.x - box.width / 2} y={box.y - box.height / 2} width={box.width} height={box.height} rx={12} />
              </clipPath>
              {/* The dots keep the map's size however much the box shrinks the country. */}
              {countries.map((country) => (
                <pattern key={country} id={`deployments-dots-${country}-${box.key}`} width={7 / box.zoom} height={7 / box.zoom} patternUnits="userSpaceOnUse">
                  <circle cx={3.5 / box.zoom} cy={3.5 / box.zoom} r={1.7 / box.zoom} fill={countryColors[country]} fillOpacity={0.42} />
                </pattern>
              ))}
              <pattern id={`deployments-dots-faint-${box.key}`} width={7 / box.zoom} height={7 / box.zoom} patternUnits="userSpaceOnUse">
                <circle cx={3.5 / box.zoom} cy={3.5 / box.zoom} r={1.1 / box.zoom} fill={Colors.lightGrey} fillOpacity={0.45} />
              </pattern>
            </React.Fragment>
          ))}
          <filter id="deployments-shadow" x="-20%" y="-30%" width="140%" height="160%">
            <feDropShadow dx={0} dy={3} stdDeviation={4} floodColor={Colors.primaryDark} floodOpacity={0.16} />
          </filter>
        </defs>
        <g mask="url(#deployments-mask)">
          {countryShapes.map(({ code, d }) => {
            const country = countries.find((candidate) => candidate === code);
            return (
              <g key={code}>
                {country && <path d={d} fill={countryColors[country]} fillOpacity={0.07} />}
                <CountryShape d={d} fill={country ? `url(#deployments-dots-${country})` : 'url(#deployments-dots-faint)'} />
              </g>
            );
          })}
          <Rivers d={riversPath} />
          {countryShapes.map(({ code, d }) => (
            <Border key={code} d={d} $strong={countries.includes(code as Country)} />
          ))}
        </g>
        {callouts
          .filter(({ inset }) => !inset)
          .map(({ site, x, y }) => (
            <CalloutLine key={site.name} x1={site.x} y1={site.y} x2={x} y2={y} />
          ))}
        <g>
          {lines.map.map(([from, to]) => (
            <ConstellationLine key={`${from.name}-${to.name}`} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
          ))}
        </g>
        {insets.map((inset) => (
          <g key={inset.key}>
            {inset.tangents.map((line, index) => (
              <InsetLine key={index} {...line} />
            ))}
            {inset.sourcePath ? <InsetSourceOutline d={inset.sourcePath} /> : <InsetSource cx={inset.sourceX} cy={inset.sourceY} r={inset.radius} />}
          </g>
        ))}
        {cities
          .filter((city) => city.outline && !insets.some((inset) => inset.key === city.key && inset.sourcePath))
          .map((city) => (
            <CityArea key={city.key} d={pathOf(city.outline!)} />
          ))}
        {mapSites.map((site, index) => (
          <SiteLink key={site.name} href={site.url} target="_blank" rel="noopener noreferrer" aria-label={site.name} $delay={index * siteDelay} {...interactions(site, true)}>
            <HitArea cx={site.x} cy={site.y} />
            <SitePoint
              cx={site.x}
              cy={site.y}
              $dimmed={!!activeNetwork && site.network !== activeNetwork}
              $highlighted={!!activeNetwork && site.network === activeNetwork}
              $hovered={site.name === hovered?.name}
            />
          </SiteLink>
        ))}
        {boxes.map((box) => (
          <g key={box.key}>
            <InsetBox x={box.x - box.width / 2} y={box.y - box.height / 2} width={box.width} height={box.height} rx={12} filter="url(#deployments-shadow)" />
            <g clipPath={`url(#deployments-box-${box.key})`}>
              {countryShapes.map(({ code, d }) => {
                const country = countries.find((candidate) => candidate === code);
                return (
                  <g key={code}>
                    {country && <path d={d} transform={box.transform} fill={countryColors[country]} fillOpacity={0.07} />}
                    <path d={d} transform={box.transform} fill={country ? `url(#deployments-dots-${country}-${box.key})` : `url(#deployments-dots-faint-${box.key})`} />
                  </g>
                );
              })}
              {countryShapes.map(({ code, d }) => (
                <InsetBorder key={code} d={d} transform={box.transform} $strong={countries.includes(code as Country)} />
              ))}
              {/* As thin as on the map, not as in the insets, as the box shrinks the country. */}
              <Rivers d={riversPath} transform={box.transform} style={{ strokeWidth: 1.3 / box.zoom }} />
            </g>
          </g>
        ))}
        {cities.map((city) => {
          const label = cityLabelPosition(city);
          return (
            <g key={city.key}>
              {!city.outline && <CityPoint cx={city.x} cy={city.y} r={city.capital ? 3.2 : 2.4} />}
              <CityLabel x={label.x} y={label.y} textAnchor={label.anchor} $capital={!!city.capital}>
                {t(`deployments.map.cities.${city.key}`)}
              </CityLabel>
            </g>
          );
        })}
        {/* Drawn over the city names, as Rijeka is right by its site. */}
        {boxSites.map((site) => (
          <SiteLink key={site.name} href={site.url} target="_blank" rel="noopener noreferrer" aria-label={site.name} $delay={mapSites.length * siteDelay} {...interactions(site, true)}>
            <HitArea cx={site.x} cy={site.y} />
            <SitePoint cx={site.x} cy={site.y} $dimmed={!!activeNetwork} $highlighted={false} $hovered={site.name === hovered?.name} />
          </SiteLink>
        ))}
        <g>
          {insets.map((inset) => (
            <g key={inset.key}>
              {inset.enlargedPath ? (
                <InsetOutline d={inset.enlargedPath} filter="url(#deployments-shadow)" />
              ) : (
                <InsetCircle cx={inset.x} cy={inset.y} r={inset.radius * inset.zoom} filter="url(#deployments-shadow)" />
              )}
              <g clipPath={`url(#deployments-inset-${inset.key})`}>
                {countryShapes.map(({ code, d }) => (
                  <InsetBorder key={code} d={d} transform={inset.transform} $strong={countries.includes(code as Country)} />
                ))}
                <InsetRiver d={riversPath} transform={inset.transform} />
                {insetCities
                  .filter((city) => city.inset === inset.key && city.outline)
                  .map((city) => (
                    <CityArea key={city.key} d={pathOf(city.outline!)} />
                  ))}
              </g>
              {landmarks
                .filter((landmark) => landmark.inset === inset.key)
                .map(({ key, x, y }) => (
                  <Landmark key={key} d={landmarkShapes[key]} transform={`translate(${x} ${y})`} />
                ))}
            </g>
          ))}
          {/* Drawn over the inset, which would otherwise hide the lines to its sites. */}
          {callouts
            .filter(({ inset }) => inset)
            .map(({ site, x, y }) => (
              <CalloutLine key={site.name} x1={site.x} y1={site.y} x2={x} y2={y} />
            ))}
          {lines.inset.map(([from, to]) => (
            <ConstellationLine key={`${from.name}-${to.name}`} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
          ))}
          {insetSites.map((site) => (
            <SiteLink key={site.name} href={site.url} target="_blank" rel="noopener noreferrer" aria-label={site.name} $delay={mapSites.length * siteDelay} {...interactions(site, true)}>
              <HitArea cx={site.x} cy={site.y} />
              <SitePoint
                cx={site.x}
                cy={site.y}
                $dimmed={!!activeNetwork && site.network !== activeNetwork}
                $highlighted={!!activeNetwork && site.network === activeNetwork}
                $hovered={site.name === hovered?.name}
              />
            </SiteLink>
          ))}
          {insetCities.map(({ key, x, y, dx, dy, outline }) => (
            <g key={key}>
              {!outline && <CityPoint cx={x} cy={y} r={2.4} />}
              <CityLabel x={x + dx} y={y + dy} textAnchor="start" $capital={false}>
                {t(`deployments.map.cities.${key}`)}
              </CityLabel>
            </g>
          ))}
        </g>
        {callouts.map(({ site, x, y }) => (
          <Callout key={site.name} href={site.url} target="_blank" rel="noopener noreferrer" tabIndex={-1} aria-hidden {...interactions(site, true)}>
            <rect
              x={x - calloutWidth / 2}
              y={y - calloutHeight / 2}
              width={calloutWidth}
              height={calloutHeight}
              rx={9}
              filter="url(#deployments-shadow)"
              stroke={site.name === hovered?.name ? Colors.primary : Colors.primaryLight}
            />
            <image
              href={`/deployments/${site.photo}`}
              x={x - calloutWidth / 2 + 9}
              y={y - calloutHeight / 2 + 6}
              width={calloutWidth - 18}
              height={calloutHeight - 12}
              preserveAspectRatio="xMidYMid meet"
            />
          </Callout>
        ))}
      </svg>
      {hovered && showCard && (
        <SiteCard
          style={hoveredCallout ? cardOverCallout(hoveredCallout) : { left: `${(hovered.x / mapSize.width) * 100}%`, top: `${(hovered.y / mapSize.height) * 100}%` }}
          $below={hovered.y < 120}
          $align={hovered.x < mapSize.width * 0.2 ? 'start' : hovered.x > mapSize.width * 0.8 ? 'end' : 'center'}
        >
          <img src={`/deployments/${hovered.photo}`} alt="" />
          <strong>{hovered.name}</strong>
          {hovered.network && <span>{t('deployments.map.partOf', { network: networkLogos[hovered.network].name })}</span>}
        </SiteCard>
      )}
    </MapFrame>
  );
};

const appear = keyframes`
  0% { opacity: 0; transform: scale(0); }
  60% { opacity: 1; transform: scale(1.6); }
  100% { opacity: 1; transform: scale(1); }
`;

// The entrance animations wait, paused, until the map scrolls into view.
const MapFrame = styled.div<{ $revealed: boolean }>`
  position: relative;
  width: 100%;
  max-width: 1100px;
  margin: 10px auto 0;

  > svg {
    display: block;
    width: 100%;
    height: auto;
  }

  svg a {
    animation-play-state: ${({ $revealed }) => ($revealed ? 'running' : 'paused')};
  }
`;

const CountryShape = styled.path`
  /* Borders show as gaps in the dots. */
  stroke: ${Colors.white};
  stroke-width: 2.5;
  stroke-linejoin: round;
`;

// A dash-dot line along the borders, as in an atlas; stronger around the countries with deployments.
const Border = styled.path<{ $strong: boolean }>`
  fill: none;
  stroke: ${Colors.darkGrey};
  stroke-opacity: ${({ $strong }) => ($strong ? 0.6 : 0.3)};
  stroke-width: 0.9;
  stroke-dasharray: 5 2 1 2;
  stroke-linejoin: round;
  pointer-events: none;
`;

const Rivers = styled.path`
  fill: none;
  stroke: #9cc0ea;
  stroke-width: 1.3;
  stroke-linejoin: round;
  stroke-linecap: round;
`;

// The river's width stays the same however much the inset enlarges it.
const InsetRiver = styled(Rivers)`
  stroke-width: 4;
  vector-effect: non-scaling-stroke;
`;

// Borders in an inset keep the map's line width and dash too.
const InsetBorder = styled(Border)`
  stroke-width: 1.2;
  vector-effect: non-scaling-stroke;
`;

const insetSourceStyle = css`
  fill: ${Colors.primary};
  fill-opacity: 0.08;
  stroke: ${Colors.primary};
  stroke-width: 1.2;
  stroke-linejoin: round;
  pointer-events: none;
`;
const InsetSource = styled.circle`
  ${insetSourceStyle}
`;
const InsetSourceOutline = styled.path`
  ${insetSourceStyle}
`;

const InsetLine = styled.line`
  stroke: ${Colors.primary};
  stroke-opacity: 0.45;
  stroke-width: 1;
  pointer-events: none;
`;

const insetStyle = css`
  fill: ${Colors.background};
  stroke: ${Colors.primary};
  stroke-width: 1.2;
  stroke-linejoin: round;
`;
const InsetCircle = styled.circle`
  ${insetStyle}
`;
const InsetOutline = styled.path`
  ${insetStyle}
`;

const InsetBox = styled.rect`
  fill: ${Colors.background};
  stroke: ${Colors.primary};
  stroke-width: 1.2;
`;

const Landmark = styled.path`
  fill: ${Colors.primaryDark};
  fill-opacity: 0.4;
  pointer-events: none;
`;

// A city drawn as its area, faintly, under the sites.
const CityArea = styled.path`
  fill: ${Colors.darkGrey};
  fill-opacity: 0.07;
  stroke: ${Colors.darkGrey};
  stroke-opacity: 0.45;
  stroke-width: 0.8;
  stroke-linejoin: round;
  pointer-events: none;
`;

const CityPoint = styled.circle`
  fill: ${Colors.white};
  stroke: ${Colors.darkGrey};
  stroke-width: 1.3;
  pointer-events: none;
`;

const CityLabel = styled.text<{ $capital: boolean }>`
  font-size: ${({ $capital }) => ($capital ? 12 : 10.5)}px;
  font-weight: ${({ $capital }) => ($capital ? 700 : 500)};
  fill: ${Colors.darkerGrey};
  paint-order: stroke;
  stroke: ${Colors.white};
  stroke-width: 3px;
  stroke-linejoin: round;
  pointer-events: none;
`;

const CalloutLine = styled.line`
  stroke: ${Colors.primary};
  stroke-width: 1;
  stroke-dasharray: 2 3;
  pointer-events: none;

  /* The callouts are hidden on phones. */
  ${Device.mobile} {
    display: none;
  }
`;

const draw = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;

const ConstellationLine = styled.line.attrs({ pathLength: 1 })`
  stroke: ${Colors.primaryDark};
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-dasharray: 1;
  animation: ${draw} 0.6s ease-out both;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Carries the entrance animation, so that it does not override the point's own opacity.
const SiteLink = styled.a<{ $delay: number }>`
  outline: none;
  cursor: pointer;
  transform-box: fill-box;
  transform-origin: center;
  animation: ${appear} 0.5s ease-out both;
  animation-delay: ${({ $delay }) => $delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// An invisible, larger target around each point, big enough for a finger on phones.
const HitArea = styled.circle.attrs({ r: 6 })`
  fill: transparent;

  ${Device.tablet} {
    r: 13px;
  }
`;

const SitePoint = styled.circle.attrs({ r: 4.5 })<{ $dimmed: boolean; $highlighted: boolean; $hovered: boolean }>`
  fill: ${Colors.primary};
  stroke: ${Colors.white};
  stroke-width: 1.5;
  transition:
    fill 0.2s,
    opacity 0.2s,
    r 0.2s;

  ${({ $dimmed }) =>
    $dimmed &&
    css`
      opacity: 0.25;
    `}

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      fill: ${Colors.primaryDark};
    `}

  ${({ $hovered }) =>
    $hovered &&
    css`
      fill: ${Colors.primaryDark};
      r: 7px;
    `}

  a:focus-visible > & {
    stroke: ${Colors.primaryDark};
    stroke-width: 3;
  }

  ${Device.mobile} {
    r: ${({ $hovered }) => ($hovered ? 10 : 6.5)}px;
  }
`;

// Not animated: Chrome does not paint an animated SVG link holding an image.
const Callout = styled.a`
  outline: none;
  cursor: pointer;

  rect {
    fill: ${Colors.white};
    stroke-width: 1;
    transition: stroke 0.2s;
  }

  &:focus-visible rect {
    stroke: ${Colors.primaryDark};
    stroke-width: 2;
  }

  /* Too small to read on phones, where the points and the card do the job. */
  ${Device.mobile} {
    display: none;
  }
`;

// The card of a site with a callout, over the callout: its logo where the callout's is
// and the name under it, or, near the bottom of the map, growing upwards from the
// callout's bottom edge; lined up with the callout's edge near the sides of the map.
const cardOverCallout = ({ x, y }: { x: number; y: number }): React.CSSProperties => {
  const align = x < mapSize.width * 0.2 ? 'start' : x > mapSize.width * 0.8 ? 'end' : 'center';
  const upwards = y > mapSize.height - 70;
  const left = { start: x - calloutWidth / 2, center: x, end: x + calloutWidth / 2 }[align];
  const top = upwards ? y + calloutHeight / 2 : y - calloutHeight / 2;
  return {
    left: `${(left / mapSize.width) * 100}%`,
    top: `${(top / mapSize.height) * 100}%`,
    transform: `translate(${{ start: '0', center: '-50%', end: '-100%' }[align]}, ${upwards ? '-100%' : '0'})`,
  };
};

// Kept within the map near its edges, where a centred card would stick out of a phone's screen.
const cardShift = { start: '-12%', center: '-50%', end: '-88%' };

const SiteCard = styled.div<{ $below: boolean; $align: 'start' | 'center' | 'end' }>`
  position: absolute;
  transform: ${({ $below, $align }) => `translate(${cardShift[$align]}, ${$below ? '18px' : 'calc(-100% - 18px)'})`};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 210px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${Colors.white};
  box-shadow: 0 10px 30px rgba(22, 44, 102, 0.18);
  color: ${Colors.primaryDark};
  font-size: 13px;
  line-height: 1.3;
  text-align: center;
  pointer-events: none;
  z-index: 1;

  img {
    max-width: 150px;
    height: 34px;
    object-fit: contain;
  }

  span {
    font-size: 12px;
    color: ${Colors.darkGrey};
  }

  ${Device.mobile} {
    width: 170px;
    padding: 10px;
    font-size: 12px;

    img {
      height: 26px;
    }
  }
`;
